const REISSUE_STATE = {
  IDLE: 'IDLE',
  REISSUING: 'REISSUING',
} as const

type ReissueState = (typeof REISSUE_STATE)[keyof typeof REISSUE_STATE]

const TRANSITION_STATUS = {
  REISSUE_START: 'REISSUE_START',
  REISSUE_SUCCESS: 'REISSUE_SUCCESS',
  REISSUE_FAIL: 'REISSUE_FAIL',
} as const

class ReissueSingleFlightScheduler {
  private state: ReissueState = REISSUE_STATE.IDLE
  private refreshPromise: Promise<unknown> | null = null
  private channel = new BroadcastChannel('token-refresh')

  constructor() {
    this.setupBroadcast()
  }

  private transition(event: keyof typeof TRANSITION_STATUS) {
    switch (event) {
      case TRANSITION_STATUS.REISSUE_START:
        this.state = REISSUE_STATE.REISSUING
        break

      case TRANSITION_STATUS.REISSUE_FAIL:
      case TRANSITION_STATUS.REISSUE_SUCCESS:
        this.state = REISSUE_STATE.IDLE
        break
    }
  }

  wait() {
    return this.refreshPromise || Promise.resolve()
  }

  async start(refreshFn: () => Promise<unknown>) {
    if (this.state !== REISSUE_STATE.IDLE) return this.wait()

    this.transition(TRANSITION_STATUS.REISSUE_START)
    this.channel.postMessage({ type: TRANSITION_STATUS.REISSUE_START })

    this.refreshPromise = refreshFn()
      .then(() => this.transition(TRANSITION_STATUS.REISSUE_SUCCESS))
      .catch((e) => {
        this.transition(TRANSITION_STATUS.REISSUE_FAIL)
        throw e
      })
      .finally(() => {
        this.finish()
      })

    return this.refreshPromise
  }

  private finish() {
    this.refreshPromise = null
    this.channel.postMessage({ type: TRANSITION_STATUS.REISSUE_SUCCESS })
  }

  private setupBroadcast() {
    this.channel.onmessage = (event) => {
      const type: keyof typeof TRANSITION_STATUS | undefined = event?.data?.type

      if (!type) return

      switch (type) {
        case TRANSITION_STATUS.REISSUE_START:
        case TRANSITION_STATUS.REISSUE_SUCCESS:
        case TRANSITION_STATUS.REISSUE_FAIL:
          this.transition(type)
          break
      }
    }
  }
}

export { ReissueSingleFlightScheduler }
